import React from 'react';
import styles from './Cards.module.css';
import { Badge, BadgeTone } from './Tags';
import { mapThumbUrl } from 'common/utils/mapThumb';

/* ══════════════════════════════════════════════════════════════════
 * PhotoGrid — 사진 3열 그리드 (모임 사진 · 후기)
 * ════════════════════════════════════════════════════════════════ */

export interface Photo {
    id: string | number;
    url: string;
    /** 대체 텍스트 — 없으면 빈 문자열(장식 이미지로 처리) */
    alt?: string;
}

interface PhotoGridProps {
    photos: Photo[];
    /** 최대 노출 장수. 초과분은 마지막 칸에 +N 으로 접힙니다 */
    max?: number;
    columns?: 2 | 3;
    onSelect?: (photo: Photo, index: number) => void;
}

export const PhotoGrid = ({ photos, max, columns = 3, onSelect }: PhotoGridProps) => {
    const limit = max ?? photos.length;
    const shown = photos.slice(0, limit);
    const rest = photos.length - shown.length;

    return (
        <div className={columns === 2 ? `${styles.photoGrid} ${styles.photoGrid2}` : styles.photoGrid}>
            {shown.map((p, i) => {
                const isLast = i === shown.length - 1 && rest > 0;
                return (
                    <button
                        key={p.id}
                        type="button"
                        className={styles.photoCell}
                        onClick={() => onSelect?.(p, i)}
                    >
                        {/* url 이 비면 <img src=""> 로 렌더하지 않습니다 — 브라우저가 현재 문서를 재요청합니다 */}
                        {p.url ? <img src={p.url} alt={p.alt || ''} loading="lazy" /> : null}
                        {isLast ? <span className={styles.photoRest}>+{rest}</span> : null}
                    </button>
                );
            })}
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * CardRail + PlaceCard — 가로 스크롤 카드 레일
 * ════════════════════════════════════════════════════════════════ */

export const CardRail = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.rail}>{children}</div>
);

interface PlaceCardProps {
    name: string;
    /** 지역·카테고리 등 보조 정보 */
    meta?: string;
    imageUrl?: string | null;
    /** 사진이 없을 때 대신 깔 지도의 좌표. 빈 회색 상자를 남기지 않습니다. */
    at?: { lat?: number | null; lng?: number | null } | null;
    /** 좌상단 배지 (예: 신규, 인기) */
    badge?: { label: string; tone?: BadgeTone };
    /** 왜 이 장소가 여기 있는지 — "지영·민수와 2번", "오후 9시까지".
     *  섹션 제목에만 근거가 있으면 스크롤 중에는 그냥 목록으로 읽힙니다. */
    evidence?: string;
    /** 광고 카드. 근거를 말할 수 없는 자리라 반드시 표시합니다 */
    sponsored?: boolean;
    wide?: boolean;
    onClick?: () => void;
}

export const PlaceCard = ({
    name, meta, imageUrl, at, badge, evidence, sponsored, wide, onClick,
}: PlaceCardProps) => (
    <button
        type="button"
        className={wide ? `${styles.place} ${styles.placeWide}` : styles.place}
        onClick={onClick}
    >
        <span className={styles.placeThumb}>
            {/* 사진 → 지도 → 빈 면. 사진이 없는 장소가 적지 않아(국내 추천은 사진을
                주지 않습니다) 빈 회색 상자 대신 그 자리의 지도를 깝니다.
                네이티브 ds PlaceCard 와 같은 방식입니다. */}
            {imageUrl ? (
                <img src={imageUrl} alt="" loading="lazy" />
            ) : mapThumbUrl(at?.lat, at?.lng) ? (
                <span className={styles.placeThumbMap}>
                    <img src={mapThumbUrl(at?.lat, at?.lng) as string} alt="" loading="lazy" />
                </span>
            ) : null}
            {badge ? (
                <span className={styles.placeBadge}>
                    <Badge tone={badge.tone || 'lime'}>{badge.label}</Badge>
                </span>
            ) : null}
        </span>
        {meta ? <span className={styles.placeMeta}>{meta}</span> : null}
        <span className={styles.placeName}>{name}</span>
        {evidence ? (
            <span className={sponsored ? `${styles.placeWhy} ${styles.placeWhyAd}` : styles.placeWhy}>
                {evidence}
            </span>
        ) : null}
    </button>
);

/* ══════════════════════════════════════════════════════════════════
 * ListCard — 썸네일 + 본문 + 우측값 (매장·장소·참여자 목록)
 * ════════════════════════════════════════════════════════════════ */

interface ListCardProps {
    title: string;
    sub?: string;
    imageUrl?: string | null;
    tags?: string[];
    /** 우측 정렬 값 — 거리, 금액 등 */
    aside?: React.ReactNode;
    /** 왜 이 항목이 여기 있는지 — "어제 방문", "평점 4.5 · 리뷰 1,204" */
    evidence?: string;
    onClick?: () => void;
}

export const ListCard = ({ title, sub, imageUrl, tags, aside, evidence, onClick }: ListCardProps) => (
    <button type="button" className={styles.list} onClick={onClick}>
        <span className={styles.listThumb}>
            {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : null}
        </span>
        <span className={styles.listBody}>
            <span className={styles.listTitle}>{title}</span>
            {sub ? <span className={styles.listSub}>{sub}</span> : null}
            {evidence ? <span className={styles.listWhy}>{evidence}</span> : null}
            {tags && tags.length > 0 ? (
                <span className={styles.listTags}>
                    {tags.map((t) => (
                        <Badge key={t} tone="neutral">{t}</Badge>
                    ))}
                </span>
            ) : null}
        </span>
        {aside ? <span className={styles.listAside}>{aside}</span> : null}
    </button>
);

/* ══════════════════════════════════════════════════════════════════
 * MeetingCard — 모이의 주 콘텐츠 단위
 * ════════════════════════════════════════════════════════════════ */

const ClockIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15 14" />
    </svg>
);

const PinIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);

interface MeetingCardProps {
    name: string;
    /** 일시 문구 — 호출부에서 i18n·포맷해 넘깁니다 */
    when?: string;
    /** 장소 문구 */
    where?: string;
    /** 우상단 시간 강조 — <TimeBadge> 를 넣습니다 */
    time?: React.ReactNode;
    /** 하단 좌측 배지들 */
    badges?: { label: string; tone?: BadgeTone }[];
    /** 하단 우측 — <AvatarStack> 등 */
    people?: React.ReactNode;
    /**
     * 장소 사진. 모임 자체에는 대표 이미지가 없으므로
     * place.externalPlaceId 로 placePhotos API 에서 받아온 URL 을 넘깁니다.
     */
    imageUrl?: string | null;
    /**
     * 장소 이름. 사진이 없을 때 썸네일 자리를 이 이름으로 채웁니다.
     * 사진 유무로 카드 모양이 갈리면 목록에서 제목 왼쪽 선이 어긋납니다.
     */
    placeName?: string | null;
    /** cover=상단 와이드 사진(강조) · thumb=좌측 작은 사진(목록) */
    media?: 'cover' | 'thumb';
    onClick?: () => void;
}

const MeetingMeta = ({ when, where }: { when?: string; where?: string }) => (
    <>
        {when ? (
            <span className={styles.meetingWhen}>
                <span className={styles.meetingIcon}><ClockIcon /></span>
                {when}
            </span>
        ) : null}
        {where ? (
            <span className={styles.meetingWhere}>
                <span className={styles.meetingIcon}><PinIcon /></span>
                {where}
            </span>
        ) : null}
    </>
);

const MeetingFoot = ({
    badges, people,
}: { badges?: { label: string; tone?: BadgeTone }[]; people?: React.ReactNode }) =>
    (badges && badges.length > 0) || people ? (
        <span className={styles.meetingBottom}>
            <span className={styles.meetingBadges}>
                {badges?.map((b) => (
                    <Badge key={b.label} tone={b.tone || 'neutral'}>{b.label}</Badge>
                ))}
            </span>
            {people}
        </span>
    ) : null;

export const MeetingCard = ({
    name, when, where, time, badges, people, imageUrl, placeName, media, onClick,
}: MeetingCardProps) => {
    const mode = imageUrl ? (media || 'thumb') : null;

    /* 사진도 장소도 없을 때만 — 썸네일 없이 */
    if (!mode && !placeName) {
        return (
            <button type="button" className={styles.meeting} onClick={onClick}>
                <span className={styles.meetingTop}>
                    <span className={styles.meetingName}>{name}</span>
                    {time}
                </span>
                <MeetingMeta when={when} where={where} />
                <MeetingFoot badges={badges} people={people} />
            </button>
        );
    }

    /* 상단 커버 — 강조용 */
    if (mode === 'cover') {
        return (
            <button
                type="button"
                className={`${styles.meeting} ${styles.meetingWithMedia}`}
                onClick={onClick}
            >
                <span className={styles.meetingCover}>
                    <img src={imageUrl as string} alt="" loading="lazy" />
                    {time ? <span className={styles.meetingCoverTime}>{time}</span> : null}
                </span>
                <span className={styles.meetingBody}>
                    <span className={styles.meetingName}>{name}</span>
                    <MeetingMeta when={when} where={where} />
                    <MeetingFoot badges={badges} people={people} />
                </span>
            </button>
        );
    }

    /* 좌측 썸네일 — 목록용.
     * 하단 줄(배지·아바타)은 오른쪽 칸이 아니라 카드 전체 폭으로 내립니다.
     * 오른쪽 칸에 두면 84px 썸네일 아래가 빈 공간으로 남습니다. */
    const foot = <MeetingFoot badges={badges} people={people} />;
    return (
        <button
            type="button"
            className={`${styles.meeting} ${styles.meetingWithMedia}`}
            onClick={onClick}
        >
            <span className={styles.meetingRow}>
                {imageUrl ? (
                    <span className={styles.meetingThumb}>
                        <img src={imageUrl as string} alt="" loading="lazy" />
                    </span>
                ) : (
                    /* 사진이 아직 없는 장소 — 자리를 비우지 않고 이름을 둡니다.
                       비우면 옆 카드와 제목 선이 어긋나고, 이름은 메타 줄의 주소와 짝이 됩니다. */
                    <span className={`${styles.meetingThumb} ${styles.meetingThumbEmpty}`}>
                        <span className={styles.meetingThumbPin}><PinIcon /></span>
                        <span className={styles.meetingThumbName}>{placeName}</span>
                    </span>
                )}
                <span className={styles.meetingRowBody}>
                    <span className={styles.meetingTop}>
                        <span className={styles.meetingName}>{name}</span>
                        {time}
                    </span>
                    <MeetingMeta when={when} where={where} />
                </span>
            </span>
            {foot ? <span className={styles.meetingFootWrap}>{foot}</span> : null}
        </button>
    );
};

/* ══════════════════════════════════════════════════════════════════
 * EmptyState — 긴 안내문에 .t-prose 굵기(300)를 쓰는 자리
 * ════════════════════════════════════════════════════════════════ */

interface EmptyStateProps {
    /** 인라인 SVG 아이콘 */
    icon?: React.ReactNode;
    title: string;
    /** copyTone 의 emptyState 문구가 들어가는 자리 */
    body?: string;
    action?: React.ReactNode;
}

export const EmptyState = ({ icon, title, body, action }: EmptyStateProps) => (
    <div className={styles.empty}>
        {icon ? <div className={styles.emptyIcon}>{icon}</div> : null}
        <p className={styles.emptyTitle}>{title}</p>
        {body ? <p className={styles.emptyBody}>{body}</p> : null}
        {action ? <div className={styles.emptyAction}>{action}</div> : null}
    </div>
);

/* ══════════════════════════════════════════════════════════════════
 * InverseBanner — copyTone 의 시간대별 문구가 놓이는 반전 배너
 * ════════════════════════════════════════════════════════════════ */

interface InverseBannerProps {
    /** copyTone.getGreeting().headline 같은 문장 */
    title: React.ReactNode;
    sub?: React.ReactNode;
    onClick?: () => void;
}

export const InverseBanner = ({ title, sub, onClick }: InverseBannerProps) => {
    const cls = onClick ? styles.inverse : `${styles.inverse} ${styles.inverseStatic}`;
    const Tag = (onClick ? 'button' : 'div') as 'button' | 'div';
    return (
        <Tag className={cls} onClick={onClick} {...(onClick ? { type: 'button' as const } : {})}>
            <span className={styles.inverseTitle}>{title}</span>
            {sub ? <span className={styles.inverseSub}>{sub}</span> : null}
        </Tag>
    );
};

/** 반전 배너 안에서 시간을 강조할 때 — 면이 뒤집혀 있어 전용 색을 씁니다. */
export const InverseTime = ({ children }: { children: React.ReactNode }) => (
    <em className={styles.inverseTime}>{children}</em>
);

/* ══════════════════════════════════════════════════════════════════
 * CardActions — 카드 바로 아래 붙는 버튼 줄
 *
 * "답해야 하는" 목록(참석 여부 · 준비 필요 · 정산 · 일정 조율)에서
 * 카드 하나 + 버튼 한 줄이 계속 반복됩니다.
 * 카드가 헤어라인으로 나뉘므로 버튼 줄이 그 선 아래에 붙습니다.
 * ════════════════════════════════════════════════════════════════ */

export const CardActions = ({ children }: { children: React.ReactNode }) => (
    <div className={`${styles.cardActions} ${styles.cardActionsAttached}`}>{children}</div>
);
